import InformationBanner from '@/components/InformationBanner';
import RegularText from '@/components/RegularText';
import Switch from '@/components/Switch';
import { Enum, PATH } from '@/constants';
import { ActionType, CriteriaType, IPTypes, SubKeyHelpCenter } from '@/constants/enum';
import options from '@/constants/options';
import { capitalizeFirstLetter, checkShowErrorInline, decimalToIP, formatCreatedAt, handleToastMutation } from '@/helpers';
import useScope from '@/hooks/Scope';
import useCountry from '@/hooks/useCountry';
import userPlans from '@/hooks/userPlans';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import { bannerSelector, handleVisibleBanner } from '@/redux/slice/banner.slice';
import { blockListSlice, settingSelector, whiteListTableSelector } from '@/redux/slice/blockList.slice';
import { dataSettingsSelector } from '@/redux/slice/dataSettings.slice';
import helpCenterSlice from '@/redux/slice/helpCenter.slice';
import toastSlice from '@/redux/slice/toast.slice';
import { IResponseApi } from '@/types/api/response.api';
import {
  Badge,
  Button,
  EmptyState,
  IndexTable,
  Link,
  Modal,
  Pagination,
  SkeletonBodyText,
  Text,
  Tooltip,
  useIndexResourceState,
} from '@shopify/polaris';
import { DeleteIcon, EditIcon } from '@shopify/polaris-icons';

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

interface TableWhiteListProps {
  onParentAction?: (action: () => void) => void;
}

const TableWhiteList = ({ onParentAction }: TableWhiteListProps): JSX.Element => {
  const navigate = useNavigate();
  const isTinyMobile = useMediaQuery({ maxWidth: 500 });
  const { t } = useTranslation(['common', 'block_redirect']);

  const { userPlanFree, userPlanPremium } = userPlans();
  const scope = useScope();
  const dispatch = useDispatch();
  const handleCountry = useCountry();
  const banner = useSelector(bannerSelector);
  const settings = useSelector(settingSelector);
  const dataSettings = useSelector(dataSettingsSelector);
  const isSkip = useSelector(isSkipApiSelector);
  const maxLimitRules = dataSettings?.settings.user.numberRuleLimit || 0;

  const [state, setState] = useState({
    itemSelected: -1,
    isOpenModalDelete: false,
  });

  const whiteListTable = useSelector(whiteListTableSelector);
  const { data, isFetching, isLoading } = apiCaller.useFetchSettingListQuery(
    {
      ...whiteListTable,
      priority: whiteListTable.priority || undefined,
    },
    { skip: isSkip },
  );
  const [activeRule, activeRuleStatus] = apiCaller.useActiveRuleMutation();
  const [deleteItem, deleteItemStatus] = apiCaller.useDeleteSettingMutation();
  const [deleteAllItem, deleteAllItemStatus] = apiCaller.useDeleteAllWhiteListSettingMutation();
  const [trackAction] = apiCaller.useTrackActionMutation();

  const ruleBlockSummary = apiCaller.useRulesSummaryQuery(
    {
      type: Enum.ActionType.Block,
      priority: Enum.ListType.BlackList,
      criteria: Enum.CriteriaType.IpAddress,
    },
    { skip: isSkip },
  );

  const handleCloseModalDelete = useCallback(() => {
    setState({
      itemSelected: -1,
      isOpenModalDelete: false,
    });
  }, []);

  const handleOpenModalDelete = useCallback(
    (id: number) => () => {
      setState({
        itemSelected: id,
        isOpenModalDelete: true,
      });
    },
    [],
  );

  const handleEdit = useCallback(
    (item: IResponseApi.SettingItem) => () => {
      if (item.criteria === CriteriaType.Country || item.criteria === CriteriaType.Province) {
        dispatch(helpCenterSlice.actions.handleSubKey(SubKeyHelpCenter.WhiteListLocation));
      } else {
        dispatch(helpCenterSlice.actions.handleSubKey(SubKeyHelpCenter.WhiteList));
      }

      const rule = {
        ...item,
        type: ActionType.WhiteList,
        country: item.country ? [item.country] : undefined,
        state: item.state ? [item.state] : undefined,
        city: item.city ? [item.city] : undefined,
        ispName: item.ispName ? [item.ispName] : undefined,
        ispCode: item.ispCode ? [item.ispCode] : undefined,
        collectionId: item.collectionId ? [item.collectionId] : [],
        productId: item.productId ? [item.productId] : [],
        pageId: item.pageId ? [item.pageId] : [],
        deviceType: item.deviceType || '',
        osName: item.osName || '',
        browserName: item.browserName ? [item.browserName] : [],
        isActive: item.isActive === undefined ? true : item.isActive,
        productName: item.productName ? [item.productName] : undefined,
        collectionName: item.collectionName ? [item.collectionName] : undefined,
        pageTitle: item.pageTitle ? [item.pageTitle] : undefined,
        ipRangeType: item?.ipRangeType,
        ipStart: item?.ipStart ? decimalToIP(BigInt(+item.ipStart), item.ipAddress.includes(':')) : '',
        ipEnd: item?.ipEnd ? decimalToIP(BigInt(+item.ipEnd), item.ipAddress.includes(':')) : '',
        userAgentBlockType: item?.userAgentType,
      };
      if (item.criteria === CriteriaType.ISP) {
        dispatch(blockListSlice.actions.handleInputIsp(item.ispName || ''));
      }
      if (item.criteria === CriteriaType.Product) {
        dispatch(blockListSlice.actions.handleInputProduct(item.productName || ''));
      }
      if (item.criteria === CriteriaType.Collection) {
        dispatch(blockListSlice.actions.handleInputCollection(item.collectionName || ''));
      }
      dispatch(blockListSlice.actions.handleErrorRule([]));
      dispatch(blockListSlice.actions.handleSetting(rule));
      dispatch(blockListSlice.actions.handleSettingBackup(rule));
      if (item.criteria === CriteriaType.IpAddress || item.criteria === CriteriaType.IpAddressStartWith || item.criteria === CriteriaType.IpRanges) {
        dispatch(blockListSlice.actions.handleChangeInputIPMethod(IPTypes.Manual));
        dispatch(blockListSlice.actions.handleSettingBackup({ ...rule, inputIPMethod: IPTypes.Manual }));
      }
      navigate(PATH.BLOCK_PAGE);
    },
    [dispatch, navigate],
  );

  const toggleRule = useCallback(
    (id: number) => (status: boolean) => {
      activeRule({
        ids: [id],
        priority: Enum.ListType.WhiteList,
        status,
      });
      dispatch(
        toastSlice.actions.handleToast({
          isOpen: true,
          content: status ? t('common:turn_on') : t('common:turn_off'),
          error: false,
        }),
      );
    },
    [activeRule, dispatch, t],
  );

  const items = useMemo(() => {
    return data?.settingList.map((blockItem) => {
      const renderDescription = () => {
        const data = [
          blockItem.ipAddress,
          blockItem.city,
          blockItem.state,
          blockItem.ispName,
          blockItem.productName,
          (blockItem.browserName && !blockItem.substring) ? blockItem.browserName : undefined,
          blockItem.osName,
          blockItem.pageTitle,
          capitalizeFirstLetter(blockItem.deviceType || ''),
          blockItem.substring,
          blockItem.collectionName,
          blockItem.country ? `${handleCountry.renderCountry(blockItem.country)} (${blockItem.country})` : undefined,
        ];
        const description = data.filter((item) => !!item).join(' - ');
        if (blockItem.referUrl) {
          const handleOpenReferralURL = () => {
            window.open(blockItem.referUrl, '_blank');
          };
          if (blockItem.shortReferUrl) {
            return (
              <Text as="span" variant="bodyMd" breakWord>
                {blockItem.country ? `${handleCountry.renderCountry(blockItem.country)} (${blockItem.country})` : undefined} -{' '}
                <Link onClick={handleOpenReferralURL} removeUnderline>
                  {blockItem.shortReferUrl}
                </Link>
              </Text>
            );
          } else {
            return (
              <Text as="span" variant="bodyMd" breakWord>
                {blockItem.country ? `${handleCountry.renderCountry(blockItem.country)} (${blockItem.country})` : undefined} -{' '}
                <Link onClick={handleOpenReferralURL} removeUnderline>
                  {blockItem.referUrl}
                </Link>
              </Text>
            );
          }
        } else return description;
      };
      return {
        id: blockItem.id.toString(),
        criteria: (
          <RegularText>
            {options.criteriaFilters(t).find((item) => item.value === blockItem.criteria)?.label as string}
          </RegularText>
        ),
        description: <RegularText>{renderDescription()}</RegularText>,
        createdAt: <RegularText>{formatCreatedAt(blockItem.createdAt, t)}</RegularText>,
        lastUpdatedAt: (
          <RegularText>{blockItem.lastUpdatedAt ? formatCreatedAt(blockItem.lastUpdatedAt * 1000, t) : ''}</RegularText>
        ),
        action: (
          <>
            {((userPlanFree &&
              ((blockItem.criteria !== Enum.CriteriaType.IpAddress &&
                blockItem.criteria !== Enum.CriteriaType.IpAddressStartWith &&
                blockItem.criteria !== Enum.CriteriaType.Country) ||
                ((ruleBlockSummary?.data?.totalRulesActivated || 0) >= maxLimitRules && blockItem.isActive === false))) ||
              (userPlanPremium &&
                (blockItem.criteria === Enum.CriteriaType.ISP ||
                  blockItem.criteria === Enum.CriteriaType.ReferralLink ||
                  blockItem.criteria === Enum.CriteriaType.IpRanges))) &&
              blockItem.isActive === false ? (
              <div
                className="pointer text-decoration"
                onClick={() => {
                  navigate(PATH.PRICING_PLAN);
                  trackAction('block_checkout_action_rule');
                }}
              >
                <Tooltip
                  content={
                    blockItem.criteria === Enum.CriteriaType.ISP ||
                      blockItem.criteria === Enum.CriteriaType.ReferralLink ||
                      blockItem.criteria === Enum.CriteriaType.IpRanges
                      ? t('common:available_enterprise_plan')
                      : t('common:available_for_premium_or_higher')
                  }
                >
                  <Badge tone="warning">{t('common:needs_upgrade')}</Badge>
                </Tooltip>
              </div>
            ) : (
              <div className={scope.isViewOnly ? 'btn-container disable' : 'btn-container'}>
                <div className="control-btn control-btn-toggle">
                  <Switch
                    onSwitch={(checked) => toggleRule(blockItem.id)(checked)}
                    isActive={blockItem.isActive}
                    isLoading={activeRuleStatus.isLoading || scope.isViewOnly}
                  />
                </div>
                <div className="absolute d-flex">
                  <div className="control-btn edit-btn">
                    <Tooltip content="Edit">
                      <Button icon={EditIcon} onClick={handleEdit(blockItem)} variant="plain" />
                    </Tooltip>
                  </div>
                  <div className="control-btn remove-btn">
                    <Tooltip content="Delete">
                      <Button icon={DeleteIcon} onClick={handleOpenModalDelete(blockItem.id)} variant="plain" />
                    </Tooltip>
                  </div>
                </div>
              </div>
            )}
          </>
        ),
      };
    });
  }, [
    data?.settingList,
    t,
    userPlanFree,
    ruleBlockSummary?.data?.totalRulesActivated,
    maxLimitRules,
    userPlanPremium,
    scope.isViewOnly,
    activeRuleStatus.isLoading,
    handleEdit,
    handleOpenModalDelete,
    handleCountry,
    navigate,
    trackAction,
    toggleRule,
  ]);

  const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } = useIndexResourceState(items || []);

  useEffect(() => {
    onParentAction?.(clearSelection);
  }, [clearSelection, onParentAction]);

  const handleDelete = useCallback(
    (id: number) => () => {
      deleteItem({ id }).then((res) => {
        const condition = checkShowErrorInline(res);
        if (!condition.status) {
          if (data && data.meta.totalItems % Number(whiteListTable.perPage) === 1) {
            dispatch(
              blockListSlice.actions.handleWhiteListTable({
                ...whiteListTable,
                page: whiteListTable.page - 1 || 1,
              }),
            );
          }
          dispatch(toastSlice.actions.handleToast(handleToastMutation(res, t)));
          handleCloseModalDelete();
          clearSelection();
        }
      });
    },
    [clearSelection, data, deleteItem, dispatch, handleCloseModalDelete, whiteListTable, t],
  );

  const handleDeleteSelected = useCallback(async () => {
    try {
      const res = await deleteAllItem({
        ids: JSON.stringify(selectedResources),
      });

      const condition = checkShowErrorInline(res);

      if (!condition.status) {
        dispatch(toastSlice.actions.handleToast(handleToastMutation(res, t)));

        if (data?.meta.itemCount === selectedResources.length) {
          dispatch(
            blockListSlice.actions.handleWhiteListTable({
              ...whiteListTable,
              page: whiteListTable.page - 1 || 1,
            }),
          );
        }

        handleCloseModalDelete();
        clearSelection();
      }
    } catch (error) {
      console.error('Error while deleting selected resources:', error);
    }
  }, [
    clearSelection,
    data?.meta.itemCount,
    deleteAllItem,
    dispatch,
    handleCloseModalDelete,
    selectedResources,
    whiteListTable,
    t,
  ]);

  const handleToggleSelected = useCallback(
    (status: boolean) => async () => {
      try {
        const res = await activeRule({
          ids: selectedResources.map((item) => +item),
          priority: Enum.ListType.WhiteList,
          status,
        });
        clearSelection();
        const condition = checkShowErrorInline(res);
        if (!condition.status) {
          dispatch(toastSlice.actions.handleToast(handleToastMutation(res, t)));
        }
      } catch (error) {
        console.log(error);
      }
    },
    [activeRule, dispatch, selectedResources, clearSelection, t],
  );

  const promotedBulkActions = [
    {
      content: t('common:cancel'),
      onAction: () => clearSelection(),
    },
    {
      content: t('common:turn_on'),
      onAction: handleToggleSelected(true),
    },
    {
      content: t('common:turn_off'),
      onAction: handleToggleSelected(false),
    },
    {
      content: t('common:delete'),
      onAction: handleOpenModalDelete(-1),
    },
  ];
  useEffect(() => {
    dispatch(blockListSlice.actions.handleOpenGuide(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const rowMarkup = useMemo(() => {
    return items?.map(({ id, criteria, description, action, createdAt, lastUpdatedAt }, index) => (
      <IndexTable.Row onClick={() => { }} id={id} key={id} position={index} selected={selectedResources.includes(id)}>
        <IndexTable.Cell>{criteria}</IndexTable.Cell>
        <IndexTable.Cell>{description}</IndexTable.Cell>
        <IndexTable.Cell>{createdAt}</IndexTable.Cell>
        <IndexTable.Cell>{lastUpdatedAt}</IndexTable.Cell>
        <IndexTable.Cell>{action}</IndexTable.Cell>
      </IndexTable.Row>
    ));
  }, [items, selectedResources]);

  const resourceName = {
    singular: 'rule',
    plural: 'rules',
  };

  return (
    <div className="pd-16">
      <InformationBanner
        onDismiss={() => {
          dispatch(
            handleVisibleBanner({
              key: 'bannerWhitelist',
              value: false,
            }),
          );
        }}
        isVisible={banner.bannerWhitelist}
      >
        <Trans t={t} i18nKey="whitelist_not_blocked" components={{ b: <b /> }} ns="block_redirect" />
      </InformationBanner>

      <div className="table-block table-block-whitelist">
        <IndexTable
          emptyState={
            isLoading ? (
              <SkeletonBodyText lines={16} />
            ) : (
              <EmptyState
                heading={t('common:take_control_website_security')}
                action={{
                  content: t('common:start_with_new_rule'),
                  onAction: () => {
                    dispatch(blockListSlice.actions.handleOpenGuide(true));
                    dispatch(blockListSlice.actions.handleTabSelectedModal(2));
                    dispatch(
                      blockListSlice.actions.handleSetting({
                        ...settings,
                        type: '0',
                      }),
                    );

                    window.scrollTo({
                      top: 0,
                      behavior: 'smooth',
                    });
                  },
                }}
                secondaryAction={{
                  content: t('common:learn_more'),
                  url: 'https://docs.ipblocker.io/',
                  target: '_blank',
                }}
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <RegularText>{t('common:set_up_whitelist')}</RegularText>
              </EmptyState>
            )
          }
          onSelectionChange={handleSelectionChange}
          resourceName={resourceName}
          itemCount={items?.length || 0}
          headings={[
            { title: t('common:criteria') },
            { title: t('common:description') },
            { title: t('common:created_at') },
            { title: t('common:last_updated') },
            { title: t('common:action') },
          ]}
          bulkActions={isTinyMobile ? promotedBulkActions : undefined}
          promotedBulkActions={!isTinyMobile ? promotedBulkActions : undefined}
          selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
        >
          {isLoading ? <SkeletonBodyText lines={16} /> : rowMarkup}
        </IndexTable>

        <div className="mt-16 pb-16">
          {data && data?.meta.totalItems > 0 ? (
            <Pagination
              label={
                data?.meta.totalItems
                  ? t('common:showing_items_range', {
                    from: (data.meta.currentPage - 1) * Number(data.meta.perPage) + 1,
                    to: Math.min(data.meta.currentPage * Number(data.meta.perPage), data.meta.totalItems),
                    total: data.meta.totalItems,
                  })
                  : null
              }
              hasPrevious={!isFetching && data && data.meta.currentPage > 1}
              onPrevious={() => {
                if (data) {
                  dispatch(
                    blockListSlice.actions.handleWhiteListTable({
                      ...whiteListTable,
                      page: data.meta.currentPage - 1,
                    }),
                  );
                }
              }}
              hasNext={
                !isFetching && data && data.meta.currentPage < Math.ceil(data?.meta.totalItems / Number(data.meta.perPage))
              }
              onNext={() => {
                if (data) {
                  dispatch(
                    blockListSlice.actions.handleWhiteListTable({
                      ...whiteListTable,
                      page: data.meta.currentPage + 1,
                    }),
                  );
                }
              }}
            />
          ) : null}
        </div>
      </div>

      <Modal
        open={state.isOpenModalDelete}
        onClose={handleCloseModalDelete}
        title={state.itemSelected === -1 ? t('common:delete_all_selected_rules') : t('common:delete_rule')}
        primaryAction={{
          destructive: true,
          content: t('common:delete'),
          onAction: state.itemSelected === -1 ? handleDeleteSelected : handleDelete(state.itemSelected),
          loading: deleteItemStatus.isLoading || deleteAllItemStatus.isLoading,
        }}
        secondaryActions={[
          {
            content: t('common:cancel'),
            onAction: handleCloseModalDelete,
          },
        ]}
      >
        <Modal.Section>
          <RegularText>{t('common:delete_rule_no_revert')}</RegularText>
        </Modal.Section>
      </Modal>
    </div>
  );
};

export default memo(TableWhiteList);
